// Accueil de l'inspecteur (artboard P04, charte D-038, « exceptions d'abord ») : ce qui demande une action
// en tête (contrôles en cours, à démarrer, saisies en conflit ou refusées, points du référentiel), puis les
// contrôles programmés et terminés ; à côté, le périmètre, la qualité de l'information et la synchronisation.
// Les missions viennent du serveur ; sans réseau, du paquet de travail conservé chiffré sur l'appareil.

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CATEGORIES_INSTALLATIONS, formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import { useSession } from '../../app/session';
import { Indisponible, MessageErreur, Squelette } from '../../composants/Etats';
import { date, dateHeure, jourLong, libelleCategorie, libelleProvince } from '../../composants/format';
import { comptesReferentiel, pluriel, QualiteInformation, usePointsReferentiel, useSituation } from '../accueil/communs';
import { aujourdhui, type LigneInspection, type PaquetInspecteur } from './api';
import { conserverPaquet, estErreurReseau, horsLigneDisponible, lirePaquet, LIBELLES_TYPES_OPERATIONS } from './horsligne';
import { BlocSynchronisation, useOperationsLocales } from './Synchronisation';
import './terrain.css';

type Onglet = 'a_faire' | 'programmes' | 'termines';

interface LigneControle {
  cle: string;
  ton: 'ambre' | 'rouge' | 'insuffisante' | 'neutre';
  marque: string;
  titre: string;
  detail: string;
  action: string;
  lien: string;
  fiche?: string;
}

/** Missions de l'inspecteur : serveur d'abord, paquet local en repli sans réseau (avec sa date). */
export function useMissionsInspecteur() {
  const [repli, setRepli] = useState<{ lignes: LigneInspection[]; enregistreLe: string } | null>(null);
  const liste = useQuery({
    queryKey: ['inspections', 'liste', 'mission=oui'],
    queryFn: async () => (await api<LigneInspection[]>('/api/v1/inspections?mission=oui')).donnees,
    retry: (n, e) => !estErreurReseau(e) && n < 1,
  });
  // Paquet de travail : rafraîchi à chaque ouverture en ligne et conservé chiffré pour le terrain.
  const paquet = useQuery({
    queryKey: ['inspections', 'paquet-travail'],
    queryFn: async () => (await api<PaquetInspecteur>('/api/v1/inspections/paquet-travail')).donnees,
    enabled: horsLigneDisponible(),
    retry: false,
  });
  useEffect(() => {
    if (paquet.data) void conserverPaquet(paquet.data).catch(() => undefined);
  }, [paquet.data]);
  useEffect(() => {
    if (!liste.error || !estErreurReseau(liste.error) || !horsLigneDisponible()) return;
    void lirePaquet().then((l) => {
      if (!l) return;
      setRepli({
        enregistreLe: l.enregistreLe,
        lignes: l.paquet.missions.map((m) => ({
          id: m.inspection.id,
          reference: m.inspection.reference,
          etat: m.inspection.etat,
          nature: 'programmee',
          objet: m.inspection.objet,
          datePrevue: m.inspection.datePrevue,
          provinceCode: m.installation?.provinceCode ?? null,
          installationId: '',
          installationNom: m.installation?.nom ?? 'Installation',
          installationCategorie: m.installation?.categorie ?? '',
          organisationNom: '',
          inspecteurs: [],
          transmiseLe: null,
          estDeMaMission: true,
        })),
      });
    });
  }, [liste.error]);
  return { liste, repli };
}

export function AccueilInspecteur() {
  const { etat } = useSession();
  const situation = useSituation();
  const pointsReferentiel = usePointsReferentiel(situation.data);
  const { liste, repli } = useMissionsInspecteur();
  const locales = useOperationsLocales();
  const [onglet, setOnglet] = useState<Onglet>('a_faire');

  const missions = liste.data ?? repli?.lignes ?? null;
  const jour = aujourdhui();
  const provinces = [...new Set(etat?.affectations?.flatMap((a) => a.perimetre.provinces) ?? [])];
  const perimetre = provinces.length ? `périmètre ${provinces.map(libelleProvince).join(', ')}` : 'périmètre national';

  const aFaire: LigneControle[] = [];
  for (const o of locales.data?.operations ?? []) {
    if (o.etat !== 'conflit' && o.etat !== 'refuse') continue;
    aFaire.push({
      cle: o.identifiantClient,
      ton: 'rouge',
      marque: o.etat === 'conflit' ? 'Synchronisation, conflit' : 'Synchronisation, refusée',
      titre: `${LIBELLES_TYPES_OPERATIONS[o.type] ?? o.type} saisi le ${dateHeure(o.horodatageEvenement)}`,
      detail: o.motif ?? (o.etat === 'conflit' ? 'Arbitrage par un validateur de la DGH' : 'Refusée par le serveur'),
      action: 'Voir la synchronisation',
      lien: '#titre-synchro',
    });
  }
  for (const m of missions ?? []) {
    if (m.etat === 'en_cours') {
      aFaire.push({ cle: m.id, ton: 'ambre', marque: 'Contrôle, en cours', titre: `${m.objet}, ${m.installationNom}`, detail: `${m.reference}, prévu le ${date(m.datePrevue)} : saisie à terminer puis rapport à transmettre`, action: 'Reprendre la saisie', lien: `/inspections/${m.id}` });
    } else if (m.etat === 'planifiee' && m.datePrevue <= jour) {
      aFaire.push({ cle: m.id, ton: 'ambre', marque: 'Contrôle, à démarrer', titre: `${m.objet}, ${m.installationNom}`, detail: `${m.reference}, prévu le ${date(m.datePrevue)}`, action: 'Ouvrir et démarrer', lien: `/inspections/${m.id}` });
    }
  }
  for (const p of pointsReferentiel) {
    aFaire.push({ cle: p.cle, ton: p.ton, marque: p.marque, titre: p.titre, detail: p.detail, action: 'Voir', lien: p.lien });
  }
  const programmes: LigneControle[] = (missions ?? [])
    .filter((m) => m.etat === 'planifiee' && m.datePrevue > jour)
    .sort((a, b) => a.datePrevue.localeCompare(b.datePrevue))
    .map((m) => ({ cle: m.id, ton: 'neutre', marque: date(m.datePrevue), titre: `${m.objet}, ${m.installationNom}`, detail: `${m.reference}, ${libelleCategorie(m.installationCategorie)}, ${libelleProvince(m.provinceCode)}`, action: 'Préparer', lien: `/inspections/${m.id}` }));
  const termines: LigneControle[] = (missions ?? [])
    .filter((m) => ['transmise', 'examinee', 'cloturee'].includes(m.etat))
    .map((m) => ({
      cle: m.id,
      ton: 'neutre',
      marque: m.transmiseLe ? `Transmis le ${date(m.transmiseLe)}` : date(m.datePrevue),
      titre: `${m.objet}, ${m.installationNom}`,
      detail: `${m.reference}, ${m.etat === 'transmise' ? 'rapport transmis, examen par la DGH en attente' : m.etat === 'examinee' ? 'rapport examiné par la DGH' : 'contrôle clôturé'}`,
      action: 'Voir le rapport',
      lien: `/inspections/${m.id}`,
    }));
  const lignes = onglet === 'a_faire' ? aFaire : onglet === 'programmes' ? programmes : termines;
  const onglets: { cle: Onglet; libelle: string; compte: number }[] = [
    { cle: 'a_faire', libelle: 'À faire', compte: aFaire.length },
    { cle: 'programmes', libelle: 'Programmés', compte: programmes.length },
    { cle: 'termines', libelle: 'Terminés', compte: termines.length },
  ];
  const titre = missions === null ? 'Mes contrôles' : aFaire.length === 0 ? 'Aucun contrôle ne vous attend.' : `${pluriel(aFaire.length, 'contrôle')} ${aFaire.length > 1 ? 'vous attendent' : 'vous attend'}.`;

  return (
    <div className="deux-colonnes accueil-role">
      <section className="principal" aria-labelledby="titre-controles">
        <div className="page-tete">
          <div>
            <span className="contexte">
              {jourLong()}, {perimetre}
            </span>
            <h1 id="titre-controles">{titre}</h1>
          </div>
          <Link className="bouton" to="/incidents/signaler">
            Signaler un incident
          </Link>
        </div>
        {repli && (
          <p className="note ambre" role="status">
            Hors connexion : missions enregistrées sur cet appareil le {dateHeure(repli.enregistreLe)}. Les saisies sont conservées chiffrées et envoyées au retour du réseau.
          </p>
        )}
        {liste.isLoading && <Squelette lignes={6} libelle="Chargement de vos contrôles" />}
        {liste.error && !repli && <MessageErreur erreur={liste.error} titre="Contrôles indisponibles" />}
        {missions && (
          <>
            <div className="segments" role="tablist" aria-label="Contrôles">
              {onglets.map((o) => (
                <button key={o.cle} type="button" role="tab" id={`onglet-${o.cle}`} aria-selected={onglet === o.cle} aria-controls="panneau-controles" onClick={() => setOnglet(o.cle)}>
                  {o.libelle}
                  <span className="compte">({o.compte})</span>
                </button>
              ))}
            </div>
            <div id="panneau-controles" role="tabpanel" aria-labelledby={`onglet-${onglet}`} className="panneau-onglet">
              {lignes.length === 0 ? (
                <p className="attenue">
                  {onglet === 'a_faire' ? 'Rien à traiter pour l’instant dans vos missions et votre périmètre.' : onglet === 'programmes' ? 'Aucun contrôle programmé à venir dans vos missions.' : 'Aucun rapport transmis dans vos missions.'}
                </p>
              ) : (
                <ol className="liste-controles">
                  {lignes.map((l) => (
                    <li key={l.cle} className={`controle-ligne ${l.ton}`}>
                      <span className="texte">
                        <span className="marque">{l.marque}</span>
                        <b>{l.titre}</b>
                        <small>{l.detail}</small>
                      </span>
                      <span className="actions">
                        {l.lien.startsWith('#') ? (
                          <a className={l.ton === 'neutre' ? 'bouton' : 'bouton primaire'} href={l.lien}>
                            {l.action}
                          </a>
                        ) : (
                          <Link className={l.ton === 'neutre' ? 'bouton' : 'bouton primaire'} to={l.lien}>
                            {l.action}
                          </Link>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <p className="lien-suite">
              <Link to="/inspections">Toutes mes missions</Link>
            </p>
          </>
        )}
      </section>

      <aside className="lateral" aria-label="Périmètre et synchronisation">
        <section className="carte-bloc" aria-labelledby="titre-perimetre">
          <h2 id="titre-perimetre">Votre périmètre</h2>
          {situation.isLoading && <Squelette lignes={3} />}
          {situation.data && !situation.data.installationsLisibles && <Indisponible motif={situation.data.motifIndisponible ?? 'accès non autorisé'} />}
          {situation.data?.installationsLisibles && <Perimetre situation={situation.data} />}
          {situation.error && <Indisponible motif="hors connexion ou serveur injoignable" />}
        </section>
        {situation.data?.installationsLisibles && <QualiteInformation situation={situation.data} />}
        <BlocSynchronisation compact />
      </aside>
    </div>
  );
}

function Perimetre({ situation }: { situation: NonNullable<ReturnType<typeof useSituation>['data']> }) {
  const c = comptesReferentiel(situation, null);
  const parCategorie = CATEGORIES_INSTALLATIONS.map((cat) => ({ cat, n: c.parCategorie(cat) })).filter((x) => x.n > 0);
  return (
    <>
      <div className="compteurs compteurs-mini" role="list" aria-label="Installations du périmètre">
        <div role="listitem">
          installations<b>{formaterNombre(c.total)}</b>
        </div>
        <div role="listitem">
          validées<b>{formaterNombre(c.valides)}</b>
        </div>
        <Link role="listitem" to="/referentiel/installations?etat=en_attente_validation">
          en attente<b className={c.enAttente > 0 ? 'ambre' : undefined}>{formaterNombre(c.enAttente)}</b>
        </Link>
      </div>
      <dl className="paires">
        {parCategorie.map(({ cat, n }) => (
          <div key={cat}>
            <dt>{libelleCategorie(cat)}</dt>
            <dd>{formaterNombre(n)}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}
